import React from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { DropzoneInputProps } from 'react-dropzone';

import { IntegrationBar } from './integration-bar';
import { MAX_UPLOAD_FILE_COUNT } from '../../../../../backend/src/constants/upload-limits';
import { RelativityIntegrationModal } from '@/new/components/dialogs/relativity/relativity-integration-dialog';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/new/components/shadcn/dialog';
import { cn } from '@/new/lib/utils';

interface FileUploaderProps {
  matterId?: string;
  subsetId?: string | null;
  disabled?: boolean;
  unpackEmail: boolean;
  deduplicateDocuments: boolean;
  fallbackToExtractedText: boolean;
  onOpenImportSettings: () => void;
  permittedFileTypes: string[];
  // Dropzone props
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  isDragActive: boolean;
  open: () => void;
  // useFileUploadLogic props
  isRelativityModalOpen: boolean;
  setIsRelativityModalOpen: (open: boolean) => void;
  connectRelativity: () => Promise<void>;
}

export function FileUploadInput({
  matterId,
  subsetId,
  disabled,
  unpackEmail,
  deduplicateDocuments,
  fallbackToExtractedText,
  onOpenImportSettings,
  permittedFileTypes,
  // Dropzone props
  getInputProps,
  isDragActive,
  open,
  // useFileUploadLogic props
  isRelativityModalOpen,
  setIsRelativityModalOpen,
  connectRelativity,
}: FileUploaderProps) {
  return (
    <>
      <input {...getInputProps()} data-testid="upload-files-input" />

      <div className="-mt-8 flex max-w-xs flex-col items-center gap-2">
        {matterId && (
          <div className={cn(disabled && 'pointer-events-none opacity-70')}>
            <IntegrationBar onRelativityConnect={connectRelativity} />
          </div>
        )}
        <div
          className={cn(
            'flex h-68 w-full flex-col items-center justify-center gap-4 rounded-xl px-12 duration-150',
            disabled
              ? 'pointer-events-none'
              : isDragActive
                ? 'border border-dashed border-blue-500 bg-white shadow-lg/8 shadow-blue-500'
                : 'border border-dashed border-gray-400 bg-white shadow-lg/1 shadow-blue-500',
          )}
        >
          <AnimatePresence mode="wait">
            {disabled ? (
              <motion.p
                key="disabled"
                initial={{ opacity: 0, filter: 'blur(2px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(2px)' }}
                transition={{ duration: 0.1 }}
                className="max-w-52 text-center text-balance text-gray-500"
              >
                <Icon className="lucide--info mr-1.5 -mb-0.5 inline-block shrink-0 text-gray-400" />
                Please wait for the current upload to finish before adding more files.
              </motion.p>
            ) : (
              <motion.div
                key="enabled"
                initial={{ opacity: 0, filter: 'blur(2px)' }}
                animate={{ opacity: 1, filter: 'blur(0px)' }}
                exit={{ opacity: 0, filter: 'blur(2px)' }}
                transition={{ duration: 0.1 }}
                className="flex flex-col items-center gap-4"
              >
                <div
                  onClick={open}
                  className={`${isDragActive ? 'border-blue-300 bg-blue-50 text-blue-400' : 'bg-gray-100 text-gray-400 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-400'} flex cursor-pointer items-center justify-center rounded-md border p-2 pb-1.75 text-lg duration-150`}
                >
                  <Icon className="lucide--upload-cloud" />
                </div>

                <p className="max-w-52 text-center text-balance text-gray-500">
                  Drag and drop documents here or{' '}
                  <Button onClick={open} className="p-0 text-blue-500 hover:text-blue-600" variant="ghost">
                    click to browse files.
                  </Button>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <Dialog>
          <DialogTrigger className="flex items-center gap-1.5 text-xs">
            <Button size="sm">
              <Icon className="lucide--file-check-2" />
              Accepted files
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                <Icon className="lucide--file-check-2" /> Accepted File
              </DialogTitle>
              <DialogDescription>The range of files currently supported by Wexler.</DialogDescription>
            </DialogHeader>

            <div className="my-5 flex flex-wrap gap-1.5 text-xs">
              {permittedFileTypes.map((ext) => (
                <span key={ext} className="rounded-md border border-gray-300 bg-gray-50 p-0.75 px-1.75 text-gray-600">
                  {ext}
                </span>
              ))}
            </div>

            <p>
              The maximum upload size is 3GB per file, and 120GB for ZIP archives. Wexler currently supports up to{' '}
              {MAX_UPLOAD_FILE_COUNT.toLocaleString()} documents in a single matter. Reach out to{' '}
              <a href="mailto:support@wexler.ai" className="text-blue-500">
                the Wexler team
              </a>{' '}
              if you have additional file requirements.
            </p>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={isRelativityModalOpen} onOpenChange={setIsRelativityModalOpen}>
        <DialogContent className="max-w-4xl">
          <RelativityIntegrationModal
            matterId={matterId}
            caseId={subsetId ?? ''}
            onClose={() => setIsRelativityModalOpen(false)}
            unpackEmail={unpackEmail}
            deduplicateDocuments={deduplicateDocuments}
            fallbackToExtractedText={fallbackToExtractedText}
            onOpenImportSettings={onOpenImportSettings}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
